import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "../../redux/states";
import { StateKeys } from "../../redux/states/main.state";
import { AppState, ResourceTypes } from "../../services/app.service";
import Main from "../../redux/actions/main.action";
import {
  ContentResponse,
  ResourceResponse,
} from "../../services/api.response.service";
import CustomVideoPlayer from "../CustomVideoPlayer";
import VideoContentItem from "./VideoContentItem";
import AutoplaySwitch from "./AutoplaySwitch";
import { getCompletedWatchCount } from "../../utils/contentProgress";

interface CourseContentWatchProps {
  content: ContentResponse;
}

const AUTOPLAY_WATCH_KEY = "ecampus_autoplay_watch";

function CourseContentWatch({ content }: CourseContentWatchProps) {
  const dispatch = useDispatch();
  const video = useSelector((state: IRootState) => state.main[StateKeys.VIDEO]);
  const [autoplay, setAutoplayState] = useState(() => {
    try {
      return localStorage.getItem(AUTOPLAY_WATCH_KEY) === "true";
    } catch {
      return false;
    }
  });
  const setAutoplay = useCallback((value: boolean) => {
    setAutoplayState(value);
    try {
      localStorage.setItem(AUTOPLAY_WATCH_KEY, value ? "true" : "false");
    } catch {
      /* ignore */
    }
  }, []);
  const [autoPlayNext, setAutoPlayNext] = useState(false);

  const videoResources = content.content_resources.filter(
    (item) => item.type === ResourceTypes.VIDEO,
  );

  // Default to first video when we have resources but no selection (or selection not in this content)
  useEffect(() => {
    if (videoResources.length === 0) return;
    const current = video.data as ResourceResponse | undefined;
    const currentId = current?.id;
    if (currentId == null || !videoResources.some((r) => r.id === currentId)) {
      dispatch(
        Main.postPageObjectDataSuccess({
          stateKey: StateKeys.VIDEO,
          data: videoResources[0],
          clear: true,
        }),
      );
      dispatch(
        Main.togglePageState({
          stateKey: StateKeys.VIDEO,
          data: AppState.LOADING,
        }),
      );
    }
  }, [content.id, videoResources.length, video.data?.id, dispatch]);

  const selectedVideo = video.data as ResourceResponse | undefined;
  const hasSelectedVideo = !!selectedVideo?.id && !!selectedVideo?.url;
  const completedWatchCount = getCompletedWatchCount(content);
  const currentIndex =
    selectedVideo?.id != null
      ? videoResources.findIndex((r) => r.id === selectedVideo.id)
      : 0;
  const upNextVideos =
    currentIndex >= 0 && currentIndex < videoResources.length - 1
      ? videoResources.slice(currentIndex + 1)
      : [];
  const showUpNext = videoResources.length > 0;

  const handleSelectVideo = useCallback(
    (resource: ResourceResponse, shouldAutoPlay = false) => {
      dispatch(
        Main.postPageObjectDataSuccess({
          stateKey: StateKeys.VIDEO,
          data: resource,
          clear: true,
        }),
      );
      dispatch(
        Main.togglePageState({
          stateKey: StateKeys.VIDEO,
          data: AppState.LOADING,
        }),
      );
      if (shouldAutoPlay) setAutoPlayNext(true);
    },
    [dispatch],
  );

  const handleVideoEnded = useCallback(() => {
    if (!autoplay || upNextVideos.length === 0) return;
    handleSelectVideo(upNextVideos[0], true);
  }, [autoplay, upNextVideos, handleSelectVideo]);

  useEffect(() => {
    if (!autoPlayNext) return;
    const id = setTimeout(() => setAutoPlayNext(false), 200);
    return () => clearTimeout(id);
  }, [autoPlayNext]);

  // When content loads and autoplay is on, start first video (runs when selected video is set for this content)
  useEffect(() => {
    if (!autoplay || !hasSelectedVideo) return;
    setAutoPlayNext(true);
  }, [content.id, autoplay, hasSelectedVideo]);

  if (videoResources.length === 0) {
    return null;
  }

  return (
    <>
      <AutoplaySwitch
        id="watch-autoplay"
        checked={autoplay}
        onChange={setAutoplay}
        label={autoplay ? "Autoplay is on" : "Autoplay is off"}
        style={{ paddingLeft: "4px" }}
      />
      {hasSelectedVideo && (
        <div className="mb-3">
          <CustomVideoPlayer
            title={
              selectedVideo.description || selectedVideo.name || content.title
            }
            src={selectedVideo.url}
            contentId={selectedVideo.content_id}
            resourceId={selectedVideo.id}
            onEnded={handleVideoEnded}
            autoPlay={autoPlayNext}
          />
        </div>
      )}

      {showUpNext && (
        <section
          className="mt-4 rounded"
          style={{
            padding: "20px 0",
            borderTop: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <h6
            className="mb-3 fw-semibold"
            style={{
              fontSize: "20px",
              lineHeight: "20px",
              fontFamily: "Poppins, sans-serif",
              color: "#3c3c3b",
              letterSpacing: "0.216px",
            }}
          >
            Up next
          </h6>
          <div className="up-next-grid pb-2">
            {videoResources.map((item, i) => {
              const isCurrentVideo = selectedVideo?.id === item.id;
              return (
                <VideoContentItem
                  key={item.id}
                  index={i + 1}
                  title={item.description || item.name || content.title}
                  subtitle={content.title}
                  thumbnailUrl={undefined}
                  onClick={() => handleSelectVideo(item, false)}
                  isActive={isCurrentVideo}
                  disabled={isCurrentVideo}
                  isCompleted={
                    completedWatchCount > 0 &&
                    videoResources.findIndex((r) => r.id === item.id) <
                      completedWatchCount
                  }
                />
              );
            })}
          </div>
        </section>
      )}
    </>
  );
}

export default CourseContentWatch;
